// Faza 7 — E-E-A-T: Author bio data za Person schema landing stranice.
// Slugovi se poklapaju sa "team.members[].slug" u i18n JSON-u (sr/en/ru).

export interface AuthorCredential { sr: string; en: string; ru: string }

export interface AuthorI18n {
  metaTitle: string;
  metaDesc: string;
  h1: string;
  role: string;
  intro: string;
  bio: string[];
  /** Sažeti spisak iskustva / fokusa */
  focus: string[];
  /** Obrazovanje (slobodan tekst po stavkama) */
  education: string[];
  /** Sertifikati / specijalizacije */
  certifications: string[];
}

export interface Author {
  slug: string;
  /** Ime i prezime — identično u svim jezicima */
  name: string;
  image: string;
  jobTitle: { sr: string; en: string; ru: string };
  /** medicalSpecialty (schema.org enum strings) */
  medicalSpecialty: string[];
  /** Interni linkovi ka uslugama koje autor sprovodi */
  relatedServices: { path: string; label: { sr: string; en: string; ru: string } }[];
  /** Reviews / mišljenja pacijenata (opcionalno) */
  reviews?: { author: string; rating: number; body: { sr: string; en: string; ru: string } }[];
  i18n: { sr: AuthorI18n; en: AuthorI18n; ru: AuthorI18n };
}

export const AUTHORS: Author[] = [
  {
    slug: 'filip-stanojevic',
    name: 'Filip Stanojević',
    image: '/images/tim/filip.webp',
    jobTitle: {
      sr: 'Strukovni fizioterapeut, specijalista funkcionalnog treninga',
      en: 'Physiotherapist, functional training specialist',
      ru: 'Физиотерапевт, специалист функционального тренинга',
    },
    medicalSpecialty: ['PhysicalTherapy', 'SportsMedicine'],
    relatedServices: [
      { path: '/fizikalna-terapija-novi-beograd', label: { sr: 'Fizikalna terapija', en: 'Physical therapy', ru: 'Физиотерапия' } },
      { path: '/services/functional-training', label: { sr: 'Funkcionalni trening', en: 'Functional training', ru: 'Функциональный тренинг' } },
      { path: '/shockwave-terapija-novi-beograd', label: { sr: 'Shockwave terapija', en: 'Shockwave therapy', ru: 'Ударно-волновая терапия' } },
    ],
    i18n: {
      sr: {
        metaTitle: 'Filip Stanojević — fizioterapeut Beograd | PHYSIOWRLD',
        metaDesc: 'Filip Stanojević — strukovni fizioterapeut i specijalista funkcionalnog treninga u PHYSIOWRLD klinici, Novi Beograd. Sportska rehabilitacija i tretman bola.',
        h1: 'Filip Stanojević — fizioterapeut i specijalista funkcionalnog treninga',
        role: 'Strukovni fizioterapeut',
        intro: 'Filip vodi protokole za sportsku rehabilitaciju, postoperativni oporavak i korekciju pokreta. Klinički pristup baziran na evidence-based fizikalnoj medicini.',
        bio: [
          'Klinički fokus na rehabilitaciji povreda donjih ekstremiteta, ACL i meniskus oporavku, kao i postoperativnom protokolu posle artroskopskih zahvata.',
          'Vodi programe funkcionalnog treninga i prevenciju povreda za rekreativne i profesionalne sportiste — trkače, fudbalere i polaznike teretane.',
        ],
        focus: [
          'Sportska rehabilitacija (ACL, meniskus, ahilova tetiva)',
          'Funkcionalni trening i korekcija pokreta',
          'Shockwave protokoli za tendinopatije',
          'Postoperativni oporavak (artroskopija)',
        ],
        education: [
          'Strukovne studije fizioterapije (Visoka zdravstvena škola)',
        ],
        certifications: [
          'Specijalista funkcionalnog treninga',
          'Shockwave AWT protokoli',
          'Kineziotaping (basic + advanced)',
        ],
      },
      en: {
        metaTitle: 'Filip Stanojević — Physiotherapist Belgrade | PHYSIOWRLD',
        metaDesc: 'Filip Stanojević — licensed physiotherapist and functional training specialist at PHYSIOWRLD clinic, New Belgrade. Sports rehabilitation and pain treatment.',
        h1: 'Filip Stanojević — physiotherapist and functional training specialist',
        role: 'Licensed physiotherapist',
        intro: 'Filip leads sports rehabilitation, post-op recovery and movement correction protocols. Evidence-based clinical approach.',
        bio: [
          'Clinical focus on lower-limb injury rehab, ACL and meniscus recovery, and post-operative protocols after arthroscopic surgery.',
          'Runs functional training and injury prevention programs for recreational and professional athletes — runners, footballers and gym goers.',
        ],
        focus: [
          'Sports rehabilitation (ACL, meniscus, Achilles)',
          'Functional training and movement correction',
          'Shockwave for tendinopathies',
          'Post-operative recovery (arthroscopy)',
        ],
        education: ['BSc Physiotherapy (College of Health Sciences)'],
        certifications: ['Functional training specialist', 'Shockwave AWT protocols', 'Kinesiotaping (basic + advanced)'],
      },
      ru: {
        metaTitle: 'Филип Станоевич — физиотерапевт Белград | PHYSIOWRLD',
        metaDesc: 'Филип Станоевич — физиотерапевт и специалист функционального тренинга в клинике PHYSIOWRLD, Новый Белград.',
        h1: 'Филип Станоевич — физиотерапевт',
        role: 'Физиотерапевт',
        intro: 'Филип ведёт протоколы спортивной реабилитации, послеоперационного восстановления и коррекции движений.',
        bio: [
          'Клинический фокус на реабилитации травм нижних конечностей, восстановлении после ACL и мениска, послеоперационных протоколах.',
          'Программы функционального тренинга и профилактики травм для бегунов, футболистов и посетителей тренажёрного зала.',
        ],
        focus: ['Спортивная реабилитация', 'Функциональный тренинг', 'УВТ при тендинопатиях', 'Послеоперационное восстановление'],
        education: ['Бакалавр физиотерапии'],
        certifications: ['Функциональный тренинг', 'УВТ-протоколы', 'Кинезиотейпирование'],
      },
    },
  },
  {
    slug: 'stefan-miljkovic',
    name: 'Stefan Miljković',
    image: '/images/tim/stefan.webp',
    jobTitle: {
      sr: 'Strukovni fizioterapeut, Ackerman kiropraktor',
      en: 'Physiotherapist, Ackerman chiropractor',
      ru: 'Физиотерапевт, хиропрактор Аккерман',
    },
    medicalSpecialty: ['PhysicalTherapy', 'Chiropractic'],
    relatedServices: [
      { path: '/kiropraktika-novi-beograd', label: { sr: 'Kiropraktika', en: 'Chiropractic', ru: 'Хиропрактика' } },
      { path: '/fizikalna-terapija-novi-beograd', label: { sr: 'Fizikalna terapija', en: 'Physical therapy', ru: 'Физиотерапия' } },
      { path: '/laser-terapija-novi-beograd', label: { sr: 'HILT laser', en: 'HILT laser', ru: 'HILT-лазер' } },
    ],
    i18n: {
      sr: {
        metaTitle: 'Stefan Miljković — kiropraktor Beograd | PHYSIOWRLD',
        metaDesc: 'Stefan Miljković — fizioterapeut i Ackerman kiropraktor u PHYSIOWRLD klinici, Novi Beograd. Korekcija posture i tretman hroničnih bolova.',
        h1: 'Stefan Miljković — fizioterapeut i Ackerman kiropraktor',
        role: 'Ackerman kiropraktor',
        intro: 'Stefan se fokusira na manuelne tehnike, korekciju posture i vraćanje pokretljivosti kod hroničnih bolova vrata i leđa.',
        bio: [
          'Kliničar sa fokusom na cervikalnu i lumbalnu kičmu — diskus hernija, lumbalni sindrom, cervikobrahijalgija, glavobolja cervikalnog porekla.',
          'Kombinuje Ackerman metod sa elementima McKenzie pristupa i HILT laser terapijom kod akutnih radikulopatija.',
        ],
        focus: [
          'Manuelna terapija kičme (Ackerman)',
          'Cervikalni i lumbalni sindrom',
          'Korekcija posture',
          'Hronični bol u vratu i leđima',
        ],
        education: [
          'Strukovne studije fizioterapije',
          'Ackerman Institute kiropraktika',
        ],
        certifications: [
          'Ackerman sertifikat za manuelnu terapiju',
          'McKenzie metod (uvod)',
        ],
      },
      en: {
        metaTitle: 'Stefan Miljković — Chiropractor Belgrade | PHYSIOWRLD',
        metaDesc: 'Stefan Miljković — physiotherapist and Ackerman chiropractor at PHYSIOWRLD clinic, New Belgrade. Posture correction and chronic pain treatment.',
        h1: 'Stefan Miljković — physiotherapist and Ackerman chiropractor',
        role: 'Ackerman chiropractor',
        intro: 'Stefan focuses on manual therapy, posture correction and mobility recovery for chronic neck and back pain.',
        bio: [
          'Clinician with focus on cervical and lumbar spine — disc herniation, lumbar syndrome, cervicobrachialgia, cervicogenic headache.',
          'Combines the Ackerman method with elements of McKenzie and HILT laser therapy for acute radiculopathies.',
        ],
        focus: ['Spinal manual therapy (Ackerman)', 'Cervical and lumbar syndrome', 'Posture correction', 'Chronic neck and back pain'],
        education: ['BSc Physiotherapy', 'Ackerman Institute Chiropractic'],
        certifications: ['Ackerman manual therapy certificate', 'McKenzie method (intro)'],
      },
      ru: {
        metaTitle: 'Стефан Милькович — хиропрактор Белград | PHYSIOWRLD',
        metaDesc: 'Стефан Милькович — физиотерапевт и хиропрактор Аккерман в клинике PHYSIOWRLD, Новый Белград.',
        h1: 'Стефан Милькович — физиотерапевт и хиропрактор Аккерман',
        role: 'Хиропрактор Аккерман',
        intro: 'Стефан занимается мануальной терапией, коррекцией осанки и восстановлением подвижности при хронических болях.',
        bio: [
          'Клинический фокус на шейном и поясничном отделах — грыжа диска, люмбаго, цервикобрахиалгия, цервикогенная головная боль.',
          'Сочетает метод Аккермана с элементами Маккензи и HILT-лазером при острых радикулопатиях.',
        ],
        focus: ['Мануальная терапия', 'Шейный и поясничный синдром', 'Коррекция осанки', 'Хроническая боль'],
        education: ['Бакалавр физиотерапии', 'Институт хиропрактики Аккерман'],
        certifications: ['Аккерман-сертификат', 'Метод Маккензи (вводный)'],
      },
    },
  },
  {
    slug: 'snezana-sainovic',
    name: 'Snežana Šainović',
    image: '/images/tim/snezana.webp',
    jobTitle: {
      sr: 'Strukovni fizioterapeut, ekspert za terapijske masaže',
      en: 'Physiotherapist, therapeutic massage expert',
      ru: 'Физиотерапевт, эксперт по терапевтическому массажу',
    },
    medicalSpecialty: ['PhysicalTherapy'],
    relatedServices: [
      { path: '/masaza-novi-beograd', label: { sr: 'Terapijska masaža', en: 'Therapeutic massage', ru: 'Терапевтический массаж' } },
      { path: '/limfna-drenaza-novi-beograd', label: { sr: 'Limfna drenaža', en: 'Lymphatic drainage', ru: 'Лимфодренаж' } },
      { path: '/presoterapija-novi-beograd', label: { sr: 'Presoterapija', en: 'Pressotherapy', ru: 'Прессотерапия' } },
    ],
    i18n: {
      sr: {
        metaTitle: 'Snežana Šainović — terapijska masaža Beograd | PHYSIOWRLD',
        metaDesc: 'Snežana Šainović — strukovni fizioterapeut i ekspert za terapijske i relaks masaže u PHYSIOWRLD klinici, Novi Beograd.',
        h1: 'Snežana Šainović — fizioterapeut i ekspert za terapijske masaže',
        role: 'Terapeut masaža',
        intro: 'Snežana radi sa pacijentima u fazi regeneracije i relaksacije mišića — manuelna limfna drenaža, sportska, anti-stres i medicinska masaža.',
        bio: [
          'Klinički fokus na manuelnoj limfnoj drenaži posle liposukcije i kod hroničnog limfedema, kao i na medicinskoj masaži kod hroničnih mišićnih tegoba.',
          'Vodi protokole anti-stres masaže i pripreme tela za sportske aktivnosti.',
        ],
        focus: [
          'Manuelna limfna drenaža (postop, limfedem)',
          'Sportska masaža',
          'Medicinska i relaks masaža',
          'Anti-stres protokoli',
        ],
        education: ['Strukovne studije fizioterapije'],
        certifications: ['Manuelna limfna drenaža', 'Sportska masaža', 'Medicinska masaža'],
      },
      en: {
        metaTitle: 'Snežana Šainović — Therapeutic massage Belgrade | PHYSIOWRLD',
        metaDesc: 'Snežana Šainović — licensed physiotherapist and therapeutic massage expert at PHYSIOWRLD clinic, New Belgrade.',
        h1: 'Snežana Šainović — physiotherapist and therapeutic massage expert',
        role: 'Massage therapist',
        intro: 'Snežana works with patients during regeneration and muscle relaxation phases — manual lymphatic drainage, sports, anti-stress and medical massage.',
        bio: [
          'Clinical focus on manual lymphatic drainage after liposuction and chronic lymphedema, plus medical massage for chronic muscular issues.',
          'Runs anti-stress and pre-sport preparation protocols.',
        ],
        focus: ['Manual lymphatic drainage', 'Sports massage', 'Medical and relax massage', 'Anti-stress protocols'],
        education: ['BSc Physiotherapy'],
        certifications: ['Manual lymphatic drainage', 'Sports massage', 'Medical massage'],
      },
      ru: {
        metaTitle: 'Снежана Шайнович — терапевтический массаж | PHYSIOWRLD',
        metaDesc: 'Снежана Шайнович — физиотерапевт и эксперт по терапевтическим массажам в клинике PHYSIOWRLD, Новый Белград.',
        h1: 'Снежана Шайнович — физиотерапевт и эксперт по массажам',
        role: 'Массажист',
        intro: 'Снежана работает с пациентами на этапе восстановления и расслабления мышц — лимфодренаж, спортивный и медицинский массаж.',
        bio: [
          'Клинический фокус на ручном лимфодренаже после липосакции и при хроническом лимфедеме.',
          'Протоколы антистрессового массажа и подготовки к спорту.',
        ],
        focus: ['Ручной лимфодренаж', 'Спортивный массаж', 'Медицинский массаж', 'Антистресс'],
        education: ['Бакалавр физиотерапии'],
        certifications: ['Ручной лимфодренаж', 'Спортивный массаж', 'Медицинский массаж'],
      },
    },
  },
];

export const AUTHOR_MAP: Record<string, Author> = Object.fromEntries(
  AUTHORS.map((a) => [a.slug, a])
);
